'use strict';

/**
 * Notes about browser support and related issues:
 *
 * Works on:
 * - macOS, Google Chrome
 * - Galaxy S6 Edge, Google Chrome, Samsung Internet, Oculus Home (GearVR),
 *   and Samsung Internet for GearVR
 * - Windows 10, Google Chrome and Microsoft Edge
 *
 * There are two issues that prevent support for Safari and Firefox. There
 * is a CORS issue on Safari, preventing video to load correctly from
 * other origins than the host. There is also a WebGL issue that occur on
 * both macOS Safari and Firefox.
 *
 * There are also problems for iOS because of the way it handle videos, with
 * automatic fullscreen etc.
 *
 * CORS issue:
 * - https://bugs.webkit.org/show_bug.cgi?id=135379
 * - https://github.com/mrdoob/three.js/issues/8110
 *
 * WebGL issue:
 * - https://github.com/mrdoob/three.js/issues/9716
 * - https://bugs.webkit.org/show_bug.cgi?id=171054
 * - https://bugzilla.mozilla.org/show_bug.cgi?id=1246410
 * - https://bugzilla.mozilla.org/show_bug.cgi?id=1322746
 *
 *
 * Does not work on IE11 because HTMLVideoElement is not a supported data
 * source in the WebGL texImage2D method. This could probably be resolved
 * with a custom fix for IE11, by rendering the video to a canvas and then
 * use the canvas as data source for texImage2D. More info at:
 * - https://msdn.microsoft.com/en-us/library/dn302435(v=vs.85).aspx
 * - https://github.com/mrdoob/three.js/issues/7560O
 *
 */

import Sizzle from 'sizzle';
import * as domUtils from '../util/dom';
import videoUtil from '../util/video-attributes';
import debug from '../util/debug';
import { VIDEO_FORMAT } from './immersive/types';
import allowImmersive from '../util/allow-immersive';
import isImmersiveStream from '../util/is-immersive-stream';
import ErrorType from '../constants/error-type';
import VideoType from "../constants/video-type";

const FULLSCREEN_CHANGE_EVENTS = 'fullscreenchange, mozfullscreenchange,' +
 'MSFullscreenChange, webkitfullscreenchange';
const NAMESPACE = 'immersive';

const Debug = debug('immersive');

function createVRButton(api, immersiveManager) {
    const button = document.createElement('div');
    button.className = 'svp_immersive-vr_button';

    domUtils.addEvents(button, 'click, touchend', function (e) {
        e.preventDefault();

        /**
         * We would like to start the video when the VRDisplay is presenting
         * the video. An issue is that play() must be called in response to a
         * user gesture. As such, api.play() is first called here, directly
         * in the event handler. The second api.play() call is when the
         * VRDisplay is ready and presenting, which is were we actually would
         * like to start playing.
         */

        api.play();
        immersiveManager.enterVR().then(() => {
            api.play();
        });
    });

    return button;
}

function isActiveImmersiveStream(videoElement, api) {
    return isImmersiveStream(api.data().videoConfig) || videoUtil.getImmersiveFormat(videoElement);
}

function getImmersiveStreams(streams) {
    return streams ? streams.filter(isImmersiveStream) : [];
}

function getNonImmersiveStreams(streams) {
    return streams ? streams.filter((stream) => !isImmersiveStream(stream)) : [];
}

function getVideoFormatOrDefault(videoElement, videoType) {
    switch(videoType) {
        case VideoType.IMMERSIVE_HLS_STEREO_TB:
        case VideoType.IMMERSIVE_DASH_STEREO_TB:
            return VIDEO_FORMAT.Stereo_TB;
        case VideoType.IMMERSIVE_HLS_STEREO_LR:
        case VideoType.IMMERSIVE_DASH_STEREO_LR:
            return VIDEO_FORMAT.Stereo_LR;
        case VideoType.IMMERSIVE_HLS_MONO:
        case VideoType.IMMERSIVE_DASH_MONO:
            return VIDEO_FORMAT.Mono;
    }

    const formatFromAttribute = videoUtil.getImmersiveFormat(videoElement);
    switch (formatFromAttribute) {
        case 'mono':
            return VIDEO_FORMAT.Mono;
        case 'stereo':
        case 'stereo_tb':
            return VIDEO_FORMAT.Stereo_TB;
        case 'stereo_lr':
            return VIDEO_FORMAT.Stereo_LR;
    }

    return VIDEO_FORMAT.Mono;
}

const SizzleSingle = (selector, context) => {
    const result = Sizzle(selector, context);

    return result.length > 0 ? result[0] : null;
};

const queryVideoElement = (api) => SizzleSingle('video', api.element());

class ImmersivePlugin {

    constructor(namespace, api) {
        this.namespace = namespace;
        this.api = api;
        this.videoElement = queryVideoElement(api);
        this.immersiveManager = null;
        this.toggleImmersiveButton = null;
        this.vrButton = null;
        this.isLive = false;

        this.api.on(this.namespace + '.metadata', (e) => {
            this.isLive = e.detail.live;

            if (isActiveImmersiveStream(this.videoElement, api)) {
                this.activeImmersiveStream = true;

                if (allowImmersive()) {
                    this.setupImmersive();
                } else {
                    this.handleImmersiveNotSupported();
                }
            } else {
                this.teardownImmersive();
                this.activeImmersiveStream = false;
            }

            if (allowImmersive() &&
                getNonImmersiveStreams(api.getStreams()).length > 0 &&
                getImmersiveStreams(api.getStreams()).length > 0 &&
                Boolean(videoUtil.getImmersiveFormat(this.videoElement)) === false) {
                this.setupToggleButton();
            }
        });
    }

    handleImmersiveNotSupported() {
        // The unknownVideoTypeError can occur after the immersiveNotSupported
        // is fired. For example, the stream might be dash only and the user
        // is using Safari. We prevent this error so that it does not replace
        // the immersive error message.
        this.api.once(this.namespace + '.' + ErrorType.UNKNOW_VIDEO_TYPE_ERROR, (e) => {
            e.preventDefault();
        });

        this.api.fire('error', {
            name: ErrorType.IMMERSIVE_NOT_SUPPORTED_ERROR
        });
    }

    setupImmersive() {
        domUtils.addClass(this.api.element(), 'svp_immersive');
        this.videoElement.crossOrigin = 'anonymous';

        // TODO: Do this only for this specific video load, so that the next
        // non-immersive video is loaded with limitBitrateByPortal set to true.
        this.api.config.limitBitrateByPortal = false;

        this.addEventListeners();

        // Dynamically import ImmersiveManager, which will also load its
        // heavy dependencies (three and webvr-polyfill).
        //
        // TODO: Use import() instead of require.ensure(), because it is
        // specific to webpack and is superseeded by ES syntax import().
        require.ensure([], (require) => {
            const ImmersiveManager = require('./immersive/immersive-manager');

            const videoFormat = getVideoFormatOrDefault(this.videoElement, this.api.data().videoConfig.type);

            Debug.log(`Video Format: ${videoFormat}`);

            this.immersiveManager = new ImmersiveManager(this.videoElement, videoFormat);

            // Return immediately if any error occured.
            if (this.immersiveManager.isInErrorState()) {
                this.api.fire('error');

                return;
            }

            this.immersiveManager.initialize().then(() => {
                // Insert VR button if VR mode is allowed.
                if (this.immersiveManager.canEnterVR()) {
                    this.vrButton = createVRButton(this.api, this.immersiveManager);
                    this.api.insertButton(this.vrButton);
                }

                this.immersiveManager.run();

                // Append to DOM
                this.api.element().appendChild(this.immersiveManager.container);

                Debug.log('Immersive plugin is enabled and running.');
            });
        }, (err) => {
            Debug.error('Failed to load immersive plugin because:', err);
            this.api.fire('error', {
                name: ErrorType.IMMERSIVE_FAILED_TO_LOAD
            });
        }, 'immersive');
    }

    teardownImmersive() {
        if (this.immersiveManager && this.immersiveManager.container) {
            this.api.element().removeChild(this.immersiveManager.container);
        }

        domUtils.removeClass(this.api.element(), 'svp_immersive');

        if (this.immersiveManager) {
            this.immersiveManager.destroy();
            this.immersiveManager = null;
        }

        if (this.vrButton) {
            this.api.removeButton(this.vrButton);
            this.vrButton = null;
        }
    }

    setupToggleButton() {
        const buttonText = `360&deg; ${this.activeImmersiveStream ? 'på' : 'av'}`;

        if (this.toggleImmersiveButton) {
            this.toggleImmersiveButton.innerHTML = buttonText;

            return;
        }

        Debug.log('Creating immersive/non-immersive toggle button');

        this.toggleImmersiveButton = document.createElement('button');
        this.toggleImmersiveButton.className = 'svp_immersive-button';
        this.toggleImmersiveButton.innerHTML = buttonText;

        domUtils.addEvents(this.toggleImmersiveButton, 'click,touchend', (e) => {
            e.stopPropagation();

            let streamType;
            if (this.activeImmersiveStream) {
                streamType = getNonImmersiveStreams(this.api.getStreams())[0].type;
            } else {
                streamType = getImmersiveStreams(this.api.getStreams())[0].type;
            }

            this.api.load(
                this.api.data().videoId,
                true,
                {
                    forceVideoType: streamType,
                    startPosition: this.isLive ? undefined : this.api.getCurrentTime()
                }
            );
        }, NAMESPACE);

        domUtils.addEvents(this.toggleImmersiveButton, 'mousedown', (e) => e.preventDefault(), NAMESPACE);

        this.api.insertButton(this.toggleImmersiveButton, {
            container: 'top-left',
            defaultStyle: false
        });
    }


    addEventListeners() {
        // Prevent automatic pause when the user enter VR mode, which occur
        // on Samsung Browser (the default Internet app).
        this.api.on(this.namespace + '.pause', (event) => {
            if (this.immersiveManager && this.immersiveManager.inVRMode()) {
                Debug.log('Applying do not pause fix');
                window.setTimeout(() => this.api.play(), 50);
            }
        });

        const handleResize = () => {
            if (this.immersiveManager && !this.immersiveManager.isInErrorState()) {
                this.immersiveManager.updateSize();
            }
        };
        this.api.on(this.namespace + '.resize', handleResize);
        domUtils.addEvents(window, 'resize', handleResize, this.namespace);
        domUtils.addEvents(document, FULLSCREEN_CHANGE_EVENTS, handleResize, this.namespace);

        // Add mouse events on window to add dragging class on the player
        // container element. This allows the CSS to change cursor when
        // dragging occurs.
        domUtils.addEvents(window, 'mousedown', () => {
            domUtils.addClass(this.api.element(), 'svp_immersive--dragging');
        }, this.namespace);
        domUtils.addEvents(window, 'mouseup', () => {
            domUtils.removeClass(this.api.element(), 'svp_immersive--dragging');
        }, this.namespace);
    }

    removeEventListeners() {
        this.api.off(this.namespace + '.metadata');
        this.api.off(this.namespace + '.resize');
        this.api.off(this.namespace + '.' + ErrorType.UNKNOW_VIDEO_TYPE_ERROR);

        domUtils.removeEvents(window, 'resize', this.namespace);
        domUtils.removeEvents(document, FULLSCREEN_CHANGE_EVENTS, this.namespace);
        domUtils.removeEvents(window, 'mousedown', this.namespace);
        domUtils.removeEvents(window, 'mouseup', this.namespace);

        if (this.toggleImmersiveButton) {
            domUtils.removeEvents(this.toggleImmersiveButton, 'click,touchend', NAMESPACE);
            domUtils.removeEvents(this.toggleImmersiveButton, 'mousedown', NAMESPACE);
        }
    }

    destroy() {
        this.removeEventListeners();
        this.teardownImmersive();
    }
}

module.exports = {
    namespace: NAMESPACE,
    _pluginInstance: null,

    create: function (api) {
        this._pluginInstance = new ImmersivePlugin(this.namespace, api);
    },

    destroy: function (api) {
        if (this._pluginInstance) {
            this._pluginInstance.destroy();
        }
    }
};



// WEBPACK FOOTER //
// ./src/js/plugins/immersive.js